import React, { useContext } from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Card,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material'
import PlaceIcon from '@mui/icons-material/Place'
import { VehicleContext } from '../SingleVehicle'
import { SectionHeader, InfoList } from './SectionKit'
import VehicleFinances from './ServicingMotInsurance/VehicleFinances'

const formatDate = (date) => (date ? moment(date).format('Do MMM YYYY') : null)

const statusChip = (label, color) =>
  label ? (
    <Chip
      size="small"
      label={label}
      color={color}
      sx={{ borderRadius: 1, fontWeight: 700 }}
    />
  ) : null

const UpcomingDeployments = ({ events }) => {
  const navigate = useNavigate()
  return (
    <Card variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
      <SectionHeader title="Upcoming Deployments" />
      {events?.length ? (
        <Stack divider={<Divider sx={{ opacity: 0.6 }} />} mt={1}>
          {events.map((deployment) => (
            <Stack
              key={deployment._id}
              py={1}
              sx={{ cursor: 'pointer' }}
              onClick={() => navigate('/events/' + deployment.linkedEvent._id)}
            >
              <Typography variant="body2" fontWeight={700}>
                {deployment.linkedEvent.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {moment(deployment.start).format('DD/MM/YYYY HH:mm')} –{' '}
                {moment(deployment.end).format('DD/MM/YYYY HH:mm')}
              </Typography>
              {deployment?.linkedEvent?.eventLocation && (
                <Stack direction="row" gap={0.5} alignItems="center">
                  <PlaceIcon fontSize="inherit" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    {deployment.linkedEvent.eventLocation}
                  </Typography>
                </Stack>
              )}
            </Stack>
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.disabled" mt={1}>
          No upcoming event deployments for this vehicle.
        </Typography>
      )}
    </Card>
  )
}

// Landing tab: everything you'd want to know about the vehicle itself —
// AmbuNet configuration, the full DVLA record, running costs, and upcoming
// event deployments.
const OverviewSection = ({ dvlaData, upcomingEvents }) => {
  const { vehicle } = useContext(VehicleContext)
  const hasDvla = dvlaData && Object.keys(dvlaData).length > 0

  const taxTone =
    dvlaData?.taxStatus === 'Taxed'
      ? 'success'
      : dvlaData?.taxStatus
        ? 'error'
        : undefined

  const motExpired =
    dvlaData?.motExpiryDate && moment(dvlaData.motExpiryDate).isBefore(moment())

  const vehicleRows = [
    { label: 'Call Sign', value: vehicle?.callSign },
    { label: 'Registration', value: vehicle?.regPlate },
    { label: 'Vehicle Type', value: vehicle?.vehicleType },
    { label: 'Base Location', value: vehicle?.baseLocation?.name },
    { label: 'Tyre Size', value: vehicle?.tyreSize },
    { label: 'Radio Codes', value: vehicle?.radioInfo },
    { label: 'Make Ready Load List', value: vehicle?.makeReadyLoadList?.title },
  ]

  const dvlaRows = [
    { label: 'Make', value: dvlaData?.make },
    { label: 'Colour', value: dvlaData?.colour },
    { label: 'Fuel Type', value: dvlaData?.fuelType },
    {
      label: 'Engine Capacity',
      value: dvlaData?.engineCapacity && `${dvlaData.engineCapacity} cc`,
    },
    {
      label: 'CO2 Emissions',
      value: dvlaData?.co2Emissions && `${dvlaData.co2Emissions} g/km`,
    },
    { label: 'Year of Manufacture', value: dvlaData?.yearOfManufacture },
    {
      label: 'First Registered',
      value:
        dvlaData?.monthOfFirstRegistration &&
        moment(dvlaData.monthOfFirstRegistration, 'YYYY-MM').format('MMM YYYY'),
    },
    { label: 'Wheel Plan', value: dvlaData?.wheelplan },
    {
      label: 'Revenue Weight',
      value: dvlaData?.revenueWeight && `${dvlaData.revenueWeight} kg`,
    },
    { label: 'Type Approval', value: dvlaData?.typeApproval },
    {
      label: 'V5C Last Issued',
      value: formatDate(dvlaData?.dateOfLastV5CIssued),
    },
    {
      label: 'Marked for Export',
      value:
        dvlaData?.markedForExport === undefined
          ? undefined
          : dvlaData.markedForExport
            ? 'Yes'
            : 'No',
    },
    {
      label: 'Tax Status',
      value: statusChip(dvlaData?.taxStatus, taxTone),
    },
    {
      label: 'Tax Due',
      value: formatDate(dvlaData?.taxDueDate),
    },
    {
      label: 'MOT Expiry',
      value: dvlaData?.motExpiryDate
        ? statusChip(
            formatDate(dvlaData.motExpiryDate),
            motExpired ? 'error' : 'success',
          )
        : undefined,
    },
  ]

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={7}>
        <Card variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
          <SectionHeader title="Vehicle Details" />
          <InfoList rows={vehicleRows} />
          <Divider sx={{ my: 1.5 }} />
          <SectionHeader
            title="DVLA Record"
            caption="Retrieved live from the DVLA using the registration plate"
          />
          {hasDvla ? (
            <InfoList rows={dvlaRows.filter((row) => row.value)} />
          ) : (
            <Alert severity="info" sx={{ mt: 1 }}>
              No DVLA data is available for this registration.
            </Alert>
          )}
        </Card>
      </Grid>
      <Grid item xs={12} md={5}>
        <Stack spacing={2}>
          <VehicleFinances />
          <UpcomingDeployments events={upcomingEvents} />
        </Stack>
      </Grid>
    </Grid>
  )
}

export default OverviewSection
